import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { stream as durableStream } from "@durable-streams/client"
import { renderStreamingMarkdown, renderMarkdown } from "../helpers/streaming_markdown"
import { escapeHtml as escapeHtmlValue } from "../helpers/escape_html"

export default class extends Controller {
  static targets = ["messagesContainer", "scrollContainer", "input"]
  static values = {
    inloopId: String,
    chatUrl: String,
    newChatUrl: String,
    threadsUrl: String
  }

  connect() {
    this.activeStreams = new Map()
    this.streamingStates = new Map()
    this.fallbackResponseIds = new Set()
    this.scrollToBottom()
    this.connectToExistingStreams()
  }

  disconnect() {
    this.activeStreams.forEach(controller => controller.abort())
    this.activeStreams.clear()
    this.streamingStates.clear()
    this.fallbackResponseIds.clear()
    this.fallbackSubscription?.unsubscribe()
  }

  connectToExistingStreams() {
    if (!this.hasMessagesContainerTarget) return

    this.messagesContainerTarget.querySelectorAll("[data-stream-url]").forEach(element => {
      if (element.dataset.streamUrl) {
        this.connectToDurableStream(element.dataset.streamUrl, element.dataset.responseId)
      }
    })
  }

  async connectToDurableStream(url, responseId = null) {
    if (!url || this.activeStreams.has(url)) return

    const controller = new AbortController()
    this.activeStreams.set(url, controller)
    try {
      const response = await durableStream({
        url,
        offset: "-1",
        live: "sse",
        json: true,
        signal: controller.signal
      })
      response.subscribeJson(batch => batch.items.forEach(item => this.handleEvent(item, responseId)))
      response.closed.then(() => this.activeStreams.delete(url))
    } catch (error) {
      if (error.name !== "AbortError") console.error("Brain chat stream failed", error)
      this.activeStreams.delete(url)
    }
  }

  subscribeToFallback(responseId = null) {
    this.fallbackResponseIds.add(responseId || "__latest__")
    if (this.fallbackSubscription) return

    this.fallbackSubscription = createConsumer().subscriptions.create(
      { channel: "BrainChatChannel", inloop_id: this.inloopIdValue },
      {
        received: data => {
          const fallbackId = this.fallbackResponseIds.size === 1 ? [...this.fallbackResponseIds][0] : null
          this.handleEvent(data, fallbackId === "__latest__" ? null : fallbackId)
          if (["complete", "error"].includes(data.type)) {
            this.fallbackResponseIds.delete(data.response_id || fallbackId || "__latest__")
            if (this.fallbackResponseIds.size === 0) {
              this.fallbackSubscription?.unsubscribe()
              this.fallbackSubscription = null
            }
          }
        }
      }
    )
  }

  handleEvent(data, fallbackResponseId = null) {
    const responseId = data.response_id || fallbackResponseId
    if (data.type === "stream") this.handleChunk(data.content, responseId)
    if (data.type === "complete") this.handleComplete(data.reply, responseId)
    if (data.type === "error") this.handleError(data.message, responseId)
  }

  handleChunk(chunk, responseId = null) {
    if (!chunk) return

    const state = this.streamingState(responseId)
    state.receivedChunk = true
    state.content += chunk
    const content = this.streamingElement(responseId)
    if (content) content.innerHTML = renderStreamingMarkdown(state.content)
    this.scrollToBottom()
  }

  handleComplete(reply, responseId = null) {
    const state = this.streamingState(responseId)
    const content = this.streamingElement(responseId)
    const finalText = state.receivedChunk ? state.content : (reply || "")
    if (content) content.innerHTML = renderMarkdown(finalText)
    const message = this.assistantMessage(responseId)
    if (message) message.dataset.rawMarkdown = finalText
    this.clearStreamingState(responseId)
    this.scrollToBottom()
  }

  handleError(message, responseId = null) {
    this.clearStreamingState(responseId)
    const content = this.streamingElement(responseId)
    if (content) content.textContent = message || "Something went wrong."
  }

  async sendMessage() {
    if (!this.hasInputTarget) return
    const message = this.inputTarget.value.trim()
    if (!message) return

    this.addUserMessage(message)
    this.inputTarget.value = ""
    this.autoResize()

    try {
      const response = await fetch(this.chatUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ message })
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.message || "Unable to get a response.")

      if (payload.messages_html) this.messagesContainerTarget.innerHTML = payload.messages_html
      if (payload.stream_url) this.connectToDurableStream(payload.stream_url, payload.response_id)
      else this.subscribeToFallback(payload.response_id)
      this.scrollToBottom()
    } catch (error) {
      this.addAssistantMessage(error.message || "Something went wrong. Please try again.")
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  autoResize() {
    if (!this.hasInputTarget) return
    this.inputTarget.style.height = "auto"
    this.inputTarget.style.height = `${Math.max(24, Math.min(this.inputTarget.scrollHeight, 200))}px`
  }

  async startNewChat() {
    if (!this.hasNewChatUrlValue) return

    try {
      const response = await fetch(this.newChatUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken() }
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.message || "Unable to start a new chat.")
      this.messagesContainerTarget.innerHTML = payload.messages_html || ""
      this.scrollToBottom()
    } catch (error) {
      this.addAssistantMessage(error.message)
    }
  }

  addUserMessage(content) {
    this.messagesContainerTarget.insertAdjacentHTML("beforeend", `
      <div class="flex justify-end">
        <div class="max-w-[540px] bg-[#FAFAFB] border border-[#F2F3F5] rounded-xl px-3 py-2 text-sm md:text-base">
          ${this.escapeHtml(content)}
        </div>
      </div>
    `)
    this.scrollToBottom()
  }

  addAssistantMessage(content) {
    this.messagesContainerTarget.insertAdjacentHTML("beforeend", `
      <div class="brain-chat-message-assistant flex flex-col gap-3">
        <img src="/logo.png" alt="AI Avatar" class="w-10 h-10 rounded-full" />
        <div class="text-sm md:text-base prose prose-sm max-w-none" data-streaming-content>${renderMarkdown(content || "")}</div>
      </div>
    `)
    this.scrollToBottom()
  }

  assistantMessage(responseId = null) {
    if (responseId && this.hasMessagesContainerTarget) {
      return [...this.messagesContainerTarget.querySelectorAll("[data-response-id]")]
        .find(element => element.dataset.responseId === responseId)
    }
    return this.lastAssistantMessage()
  }

  lastAssistantMessage() {
    return this.messagesContainerTarget?.querySelector(".brain-chat-message-assistant:last-child")
  }

  streamingElement(responseId = null) {
    return this.assistantMessage(responseId)?.querySelector("[data-streaming-content]")
  }

  streamingState(responseId = null) {
    const key = responseId || "__latest__"
    if (!this.streamingStates.has(key)) {
      this.streamingStates.set(key, { content: "", receivedChunk: false })
    }
    return this.streamingStates.get(key)
  }

  clearStreamingState(responseId = null) {
    this.streamingStates.delete(responseId || "__latest__")
  }

  scrollToBottom() {
    const container = this.hasScrollContainerTarget ? this.scrollContainerTarget : this.messagesContainerTarget
    if (container) requestAnimationFrame(() => { container.scrollTop = container.scrollHeight })
  }

  escapeHtml(text) {
    return escapeHtmlValue(text)
  }

  csrfToken() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }
}
